import { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { FormApi } from "final-form";
import { Switches } from "mui-rff";
import { Field, Form, useField } from "react-final-form";

import { useTranslation } from "@/lib/i18n";
import {
  useDashboardMaritimeWarningSettingsQuery,
  useUpdateDashboardMaritimeWarningSettingsMutation,
} from "@/features/admin/features/dashboards";
import { useAdminDashboard } from "@/features/admin/hooks";
import { showToast, TextField } from "@/features/shared/components";
import {
  useMaritimeWarningPageDefaults,
  useToolbarLabelWithCode,
} from "@/features/shared/hooks/use-warning-page-defaults";
import { maritimeToolbarLevel } from "@/lib/warning-page-copy";
import type { MaritimeWarningDomainId } from "@/lib/maritime-domains";
import {
  isMissingRequiredMaritimeDomain,
  maritimeDomainIdsToSave,
  maritimeWarningSettingsUpdateBody,
  type MaritimeWarningPageSettings,
} from "@/lib/maritime-warning-page-settings.utils";

const DOMAINS: { id: MaritimeWarningDomainId; i18nKey: "coastal" | "north-sea" }[] = [
  { id: "coastal", i18nKey: "coastal" },
  { id: "north-sea", i18nKey: "north-sea" },
];

type CopyFields = Pick<
  MaritimeWarningPageSettings,
  "enabled" | "toolbarLabel" | "title" | "description"
>;

/**
 * One boolean per domain instead of a `domainIds` array: final-form compares
 * `initialValues` shallowly, so a fresh array each render would reset the form.
 */
type FormValues = CopyFields & Record<MaritimeWarningDomainId, boolean>;

const selectedDomainIds = (values: FormValues): MaritimeWarningDomainId[] =>
  DOMAINS.filter(({ id }) => values[id]).map(({ id }) => id);

/** Domain row: label and note left, switch right. Only changes form state. */
function DomainSwitchRow({
  name,
  label,
  note,
}: {
  name: MaritimeWarningDomainId;
  label: string;
  note: string;
}) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      spacing={2}
      sx={{ px: "2px" }}
    >
      <Typography>
        {label}
        <small style={{ color: grey[500], display: "block" }}>{note}</small>
      </Typography>
      <Field<boolean> name={name} type="checkbox">
        {({ input }) => (
          <Switch
            name={input.name}
            checked={input.checked ?? false}
            onChange={input.onChange}
            inputProps={{ "aria-label": label }}
          />
        )}
      </Field>
    </Stack>
  );
}

function MaritimeWarningFields() {
  const { t } = useTranslation();
  const enabled = useField<boolean>("enabled", {
    type: "checkbox",
    subscription: { value: true },
  }).input.checked;
  const toolbarLabel = useField<string | undefined>("toolbarLabel", {
    subscription: { value: true },
  }).input.value;
  const withCode = useToolbarLabelWithCode();

  return (
    <Stack spacing={2} sx={{ pt: 1 }}>
      <Switches
        name="enabled"
        data={{
          label: t("admin.dashboard_settings.maritime_warnings.enabled"),
          value: true,
        }}
      />
      {/* Domains only matter for a visible page; their values are kept while hidden. */}
      {enabled &&
        DOMAINS.map(({ id, i18nKey }) => (
          <DomainSwitchRow
            key={id}
            name={id}
            label={t(`admin.dashboard_settings.maritime_warnings.${i18nKey}.label`)}
            note={t(`admin.dashboard_settings.maritime_warnings.${i18nKey}.note`)}
          />
        ))}
      <TextField
        name="title"
        label={t("admin.dashboard_settings.warning_pages.fields.title")}
        placeholder={t("dashboard.warnings.maritime.defaults.title")}
      />
      <TextField
        name="description"
        label={t("admin.dashboard_settings.warning_pages.fields.description")}
        placeholder={t("dashboard.warnings.maritime.defaults.description")}
        multiline
        minRows={2}
      />
      <TextField
        name="toolbarLabel"
        label={t("admin.dashboard_settings.warning_pages.fields.alert_button_label")}
        placeholder={t("dashboard.warnings.maritime.defaults.toolbar")}
        helperText={t("admin.dashboard_settings.maritime_warnings.alert_button_help", {
          example: withCode(
            toolbarLabel || t("dashboard.warnings.maritime.defaults.toolbar"),
            maritimeToolbarLevel(t("code.yellow")),
          ),
        })}
      />
    </Stack>
  );
}

function DomainsRequiredDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        {t("admin.dashboard_settings.maritime_warnings.domains_required.title")}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t("admin.dashboard_settings.maritime_warnings.domains_required.body")}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} autoFocus>
          {t("general.confirm.ok")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function MaritimeWarningForm() {
  const { t } = useTranslation();
  const dashboard = useAdminDashboard();
  const settingsQuery = useDashboardMaritimeWarningSettingsQuery(dashboard?.id ?? "");
  const updateSettings = useUpdateDashboardMaritimeWarningSettingsMutation();
  const defaults = useMaritimeWarningPageDefaults();
  const [domainsRequiredOpen, setDomainsRequiredOpen] = useState(false);
  const settings = settingsQuery.data;

  if (settingsQuery.isError) {
    return (
      <Alert severity="error">
        {t("admin.dashboard_settings.maritime_warnings.error")}
      </Alert>
    );
  }
  if (!dashboard || settingsQuery.isLoading || !settings) return null;

  const initialValues: FormValues = {
    enabled: settings.enabled,
    toolbarLabel: settings.toolbarLabel,
    title: settings.title,
    description: settings.description,
    coastal: settings.domainIds.includes("coastal"),
    "north-sea": settings.domainIds.includes("north-sea"),
  };

  // Everything saves together on submit, nothing on toggle.
  const onSubmit = (values: FormValues, form: FormApi<FormValues>) => {
    const enabled = values.enabled === true;
    const domainIds = selectedDomainIds(values);

    if (isMissingRequiredMaritimeDomain(enabled, domainIds)) {
      setDomainsRequiredOpen(true);
      return;
    }

    updateSettings.mutate(
      {
        dashboardId: dashboard.id,
        body: maritimeWarningSettingsUpdateBody(
          {
            enabled,
            domainIds: maritimeDomainIdsToSave(domainIds, settings.domainIds),
            toolbarLabel: values.toolbarLabel,
            title: values.title,
            description: values.description,
          },
          defaults,
        ),
      },
      {
        onSuccess: () => {
          showToast(t("admin.messages.dashboard_updated"));
          form.reset(values);
        },
        onError: () => {
          showToast(t("admin.dashboard_settings.maritime_warnings.error"));
        },
      },
    );
  };

  return (
    <Stack spacing={1}>
      <Form<FormValues>
        onSubmit={onSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, pristine }) => (
          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <MaritimeWarningFields />
              <Stack alignItems="flex-end">
                <Button
                  type="submit"
                  variant="outlined"
                  disabled={pristine || updateSettings.isPending}
                >
                  {t("admin.dashboard_settings.warning_pages.save")}
                </Button>
              </Stack>
            </Stack>
          </form>
        )}
      />
      <DomainsRequiredDialog
        open={domainsRequiredOpen}
        onClose={() => setDomainsRequiredOpen(false)}
      />
    </Stack>
  );
}

const MaritimeWarningsSettings = () => (
  <Stack spacing={4} sx={{ py: 1 }}>
    <MaritimeWarningForm />
  </Stack>
);

export default MaritimeWarningsSettings;
